/**
 *       **** Readme ****
 *
 * Component: <FormSelect />
 * Purpose: Returns the Select element.
 *
 * Props:
 *   -required: {Boolean} Indicates if the field is required
 *   -showRequired: {Boolean} Indicates whether the "*" or the "optional" text is displayed
 *
 *
 * Creation date: 01/March/2022
 * Last update: 04/April/2022
 */

import React, { useEffect } from "react";
import { useField } from "formik";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

//Components
import selectStyles from "../GlobalTools";

// Custom react-select option with edit/delete actions
const CustomOption = (props) => {
  const { data = {}, isFocused } = props;

  if (data.value === "add") {
    return <components.Option {...props}>{data.label}</components.Option>;
  }

  return (
    <components.Option {...props}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <span>{data.label}</span>

        <span
          className="option-actions"
          style={{
            display: "flex",
            gap: "10px",
            opacity: isFocused ? 1 : 0,
            visibility: isFocused ? "visible" : "hidden",
            transition: "opacity 0.2s ease",
          }}
        >
          {data.onEdit && (
            <i
              className="bi bi-pencil-fill"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                props.selectProps.onMenuClose();
                data.onEdit();
              }}
            />
          )}

          {data.onDelete && (
            <i
              className="bi bi-trash-fill"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                props.selectProps.onMenuClose();
                data.onDelete();
              }}
            />
          )}
        </span>
      </div>
    </components.Option>
  );
};
export default function FormSelect({ label, ...props }) {
  const [t] = useTranslation(["global"]);
  const [field, meta, helpers] = useField(props);

  /**
   * Initial loading
   */
  useEffect(() => {}, []);

  return (
    <Form.Group className={props.className || "mb-3"}>
      {label && (
        <Form.Label
          htmlFor={props.id || props.name}
          className={props.required && props.showRequired ? "required" : ""}
        >
          {label}
          {!props.required && props.showRequired === false ? (
            <span className="optional">{t("optional")}</span>
          ) : (
            ""
          )}
        </Form.Label>
      )}
      <Select
        classNamePrefix={"select"}
        className={
          meta.touched && meta.error ? `select errorValidation` : `select`
        }
        styles={selectStyles}
        {...field}
        {...props}
        onBlur={() => helpers.setTouched(true)}
        noOptionsMessage={() => t("select.noMatches")}
        loadingMessage={() => t("select.searching")}
        components={{
          Option: CustomOption,
          DropdownIndicator: (propsIndicator) => (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                {props.searchIndicator ? (
                  <span
                    className="bi bi-search"
                    style={{
                      fontSize: 18,
                      marginLeft: 6,
                      marginRight: 6,
                      color: "#b8bcbd",
                    }}
                  ></span>
                ) : (
                  /* Default dropdown indicator */
                  <components.DropdownIndicator {...propsIndicator} />
                )}
              </div>
            </>
          ),
        }}
      />
      {meta.touched && meta.error ? (
        <Form.Text className="text-muted error">{meta.error}</Form.Text>
      ) : null}
    </Form.Group>
  );
}

FormSelect.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  required: PropTypes.bool,
  showRequired: PropTypes.bool,
  className: PropTypes.string,
  searchIndicator: PropTypes.bool,
};

CustomOption.propTypes = {
  data: PropTypes.object,
  selectProps: PropTypes.shape({
    onMenuClose: PropTypes.func,
  }),
  isFocused: PropTypes.bool,
};
